/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, FieldArray } from 'formik';
import { Row, Col, Button } from 'antd';
import { Select } from 'formik-antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { Box, FormInput, CustomInput } from 'atoms';

const UOM = [
	{ label: 'Packet', value: 'pkt' },
	{ label: 'Boxes', value: 'bxs' },
	{ label: 'Bundle', value: 'bnd' },
	{ label: 'Carton', value: 'ctn' },
	{ label: 'Dozen', value: 'dzn' },
	{ label: 'Gross', value: 'grs' },
	{ label: 'Lot', value: 'lot' },
	{ label: 'Rack', value: 'rck' },
	{ label: 'Roll', value: 'rll' },
	{ label: 'Sheet', value: 'sht' },
];

// Constants for repeated strings
const DECLARED_VALUE_LABEL = 'Declared Value*';
const DECLARED_VALUE_PLACEHOLDER = 'Declared Value';
const DESCRIPTION_PLACEHOLDER = 'Description';
const DESCRIPTION_OF_GOODS_LABEL = 'Description of Goods*';

// Constants for repeated styles
const BORDER_STYLE = '1px solid #C6C6C8';
const BOX_BORDER_STYLE = { border: BORDER_STYLE, padding: '10px' };
const BOX_BORDER_BG_STYLE = {
	border: BORDER_STYLE,
	padding: '10px',
	backgroundColor: '#EFF8FF',
};

export default function CreateLRForm({
	values,
	isSubmitting,
	isValid,
	setFieldValue,
	handleSubmit,
	handlePincodeChange,
	validateEwbNumber,
	handleBackNavigation,
	isCreateLRWithoutId,
	isEditMode,
}) {
	const handlePreviousClick = () => {
		if (isCreateLRWithoutId && handleBackNavigation) {
			handleBackNavigation(values);
		} else {
			window.history.back();
		}
	};

	useEffect(() => {
		window.addEventListener('breadcrumb-back-click', handlePreviousClick);
		return () => window.removeEventListener('breadcrumb-back-click', handlePreviousClick);
	});

	return (
		<Form
			onSubmit={handleSubmit}
			onKeyDown={(e) => {
				// Prevent Enter from submitting unless in textarea or tag input
				if (
					e.key === 'Enter' &&
					e.target.type !== 'textarea' &&
					e.target.name !== '_refInput'
				) {
					e.preventDefault();
				}
			}}
		>
			<Box padding="1.5rem 2rem" background="#fff">
				<Row gutter={32} style={{ marginBottom: '16px' }}>
					<Col lg={12} style={{ textAlign: 'center' }}>
						<Box style={BOX_BORDER_STYLE}>
							<h3 style={{ fontWeight: 'bold', fontSize: '24px', padding: '10px' }}>
								Goods Summary
							</h3>
							<Row gutter={24}>
								<Col lg={12}>
									<FormInput
										name="goods_description"
										label={DESCRIPTION_OF_GOODS_LABEL}
									>
										<CustomInput
											name="goods_description"
											placeholder={DESCRIPTION_PLACEHOLDER}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="declared_value" label={DECLARED_VALUE_LABEL}>
										<CustomInput
											name="declared_value"
											placeholder={DECLARED_VALUE_PLACEHOLDER}
											isCaps
											type="number"
										/>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={24}>
								<Col lg={12}>
									<FormInput name="gross_weight" label="Gross Weight*">
										<CustomInput
											name="gross_weight"
											placeholder="Gross Weight in Ton"
											isCaps
											type="number"
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="unit_count" label="Unit Count">
										<CustomInput
											name="unit_count"
											placeholder={DECLARED_VALUE_PLACEHOLDER}
											isCaps
											type="number"
										/>
									</FormInput>
								</Col>
							</Row>
						</Box>
					</Col>
					<Col lg={12} style={{ textAlign: 'center', justifyContent: 'flex-start' }}>
						<Box style={BOX_BORDER_STYLE}>
							<h3
								style={{
									fontWeight: 'bold',
									fontSize: '24px',
									paddingInline: '10px',
								}}
							>
								Reference
							</h3>
							<FieldArray
								name="client_reference_numbers"
								render={({ remove, push }) => {
									const tags = values.client_reference_numbers || [];
									return (
										<>
											<div
												style={{
													display: 'flex',
													gap: 8,
													marginBottom: 12,
												}}
											>
												<input
													type="text"
													value={values._refInput || ''}
													placeholder="Reference Number"
													style={{
														flex: 1,
														padding: '8px',
														border: BORDER_STYLE,
														borderRadius: 4,
													}}
													onChange={(e) =>
														setFieldValue('_refInput', e.target.value)
													}
													onKeyDown={(e) => {
														if (
															e.key === 'Enter' &&
															values._refInput?.trim()
														) {
															if (
																!tags.includes(
																	values._refInput.trim()
																)
															) {
																push(values._refInput.trim());
															}
															setFieldValue('_refInput', '');
														}
													}}
												/>
												<button
													type="button"
													style={{
														background: '#143e60',
														color: 'white',
														border: 'none',
														borderRadius: 4,
														padding: '0 24px',
														height: 40,
														fontWeight: 500,
														cursor: 'pointer',
													}}
													onClick={() => {
														if (values._refInput?.trim()) {
															if (
																!tags.includes(
																	values._refInput.trim()
																)
															) {
																push(values._refInput.trim());
															}
															setFieldValue('_refInput', '');
														}
													}}
												>
													Add
												</button>
											</div>
											<div
												style={{
													display: 'flex',
													overflowX: 'auto',
													gap: 8,
													padding: 4,
													border: BORDER_STYLE,
													borderRadius: 4,
													background: '#fff',
													marginBottom: 12,
													minHeight: 44,
													alignItems: 'center',
													scrollbarWidth: 'none',
													msOverflowStyle: 'none',
												}}
												className="reference-tag-scroll"
											>
												{(values.client_reference_numbers || []).map(
													(tag, idx) =>
														tag !== '' && (
															<span
																key={`ref-${tag}`}
																style={{
																	display: 'inline-flex',
																	alignItems: 'center',
																	background: '#fff',
																	border: '1px solid #143e60',
																	color: '#143e60',
																	borderRadius: 10,
																	padding: '4px 12px',
																	fontSize: 14,
																	marginRight: 0,
																	whiteSpace: 'nowrap',
																	fontWeight: 500,
																}}
															>
																{tag}
																<button
																	type="button"
																	style={{
																		marginLeft: 8,
																		cursor: 'pointer',
																		fontWeight: 'bold',
																		background: 'none',
																		border: 'none',
																		color: 'inherit',
																		fontSize: 'inherit',
																		padding: 0,
																	}}
																	disabled={isEditMode}
																	onClick={() => remove(idx)}
																	aria-label={`Remove ${tag}`}
																>
																	×
																</button>
															</span>
														)
												)}
											</div>
										</>
									);
								}}
							/>
							<Row gutter={24} style={{ marginTop: 8 }}>
								<Col lg={24}>
									<FormInput name="ewb_number" label="EWay Bill No.">
										<CustomInput
											name="ewb_number"
											placeholder="EWay Bill No."
											isCaps
											maxLength={12}
											onBlur={(e) =>
												setFieldValue('ewb_number', e.target.value)
											}
										/>
										{values.ewb_number &&
											validateEwbNumber(values.ewb_number) && (
												<div style={{ color: 'red', marginTop: 4 }}>
													{validateEwbNumber(values.ewb_number)}
												</div>
											)}
									</FormInput>
								</Col>
							</Row>
						</Box>
					</Col>
				</Row>
				<Row gutter={32} style={{ marginBottom: '16px' }}>
					<Col lg={12} style={{ textAlign: 'center' }}>
						<Box style={BOX_BORDER_BG_STYLE}>
							<h3 style={{ fontWeight: 'bold', fontSize: '24px', padding: '10px' }}>
								Sender
							</h3>
							<Row gutter={24}>
								<Col lg={24}>
									<FormInput name="sender_name" label="Name*">
										<CustomInput name="sender_name" placeholder="Name" />
									</FormInput>
								</Col>
							</Row>
							<Row gutter={24}>
								<Col lg={24}>
									<FormInput name="sender_address" label="Address*">
										<CustomInput name="sender_address" placeholder="Address" />
									</FormInput>
								</Col>
							</Row>
							<Row gutter={24}>
								<Col lg={12}>
									<FormInput name="sender_pincode" label="Pincode*">
										<CustomInput
											name="sender_pincode"
											placeholder="Pincode"
											disabled
											isCaps
											maxLength={6}
											onChange={(e) => {
												setFieldValue('sender_pincode', e.target.value);
												handlePincodeChange(
													e.target.value,
													'source',
													setFieldValue
												);
											}}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="source_location" label="City, State">
										<CustomInput
											name="source_location"
											placeholder="City, State"
											disabled
										/>
									</FormInput>
								</Col>
							</Row>
						</Box>
					</Col>
					<Col lg={12} style={{ textAlign: 'center' }}>
						<Box style={BOX_BORDER_BG_STYLE}>
							<h3 style={{ fontWeight: 'bold', fontSize: '24px', padding: '10px' }}>
								Receiver
							</h3>
							<Row gutter={24}>
								<Col lg={24}>
									<FormInput name="receiver_name" label="Name*">
										<CustomInput name="receiver_name" placeholder="Name" />
									</FormInput>
								</Col>
							</Row>
							<Row gutter={24}>
								<Col lg={24}>
									<FormInput name="receiver_address" label="Address*">
										<CustomInput
											name="receiver_address"
											placeholder="Address"
										/>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={24}>
								<Col lg={12}>
									<FormInput name="receiver_pincode" label="Pincode*">
										<CustomInput
											name="receiver_pincode"
											placeholder="Pincode"
											isCaps
											disabled
											maxLength={6}
											onChange={(e) => {
												setFieldValue('receiver_pincode', e.target.value);
												handlePincodeChange(
													e.target.value,
													'destination',
													setFieldValue
												);
											}}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="destination_location" label="City, State">
										<CustomInput
											name="destination_location"
											placeholder="City, State"
											disabled
										/>
									</FormInput>
								</Col>
							</Row>
						</Box>
					</Col>
				</Row>
				<FieldArray
					name="goods_details"
					render={({ remove, push }) => (
						<>
							{values.goods_details?.map((goods_details, index) => (
								<Row gutter={32} style={{ marginBottom: '16px' }} key={index}>
									<Col lg={24} style={{ textAlign: 'center' }}>
										<Box
											style={{
												border: '1px solid #C6C6C8',
												padding: '10px',
												backgroundColor: '#EFF8FF',
											}}
										>
											<h3
												style={{
													fontWeight: 'bold',
													fontSize: '24px',
													padding: '10px',
												}}
											>
												Goods Details
											</h3>
											<Row gutter={24}>
												<Col lg={12}>
													<FormInput
														name={`goods_details[${index}].goods_description`}
														label="Description*"
														labelCol={{ span: 9.5 }}
														wrapperCol={{ span: 14.5 }}
													>
														<CustomInput
															name={`goods_details[${index}].goods_description`}
															placeholder="Description"
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_quantity`}
														label="Quantity"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<CustomInput
															type="number"
															name={`goods_details[${index}].goods_quantity`}
															placeholder="Quantity"
															isCaps
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_uom`}
														label="UOM"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<Select
															name={`goods_details[${index}].goods_uom`}
															showSearch
															filterOption={(input, option) =>
																option.label
																	.toLowerCase()
																	.indexOf(input.toLowerCase()) >=
																0
															}
															options={UOM}
															placeholder="UOM"
															style={{ width: '100%' }}
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_length`}
														label="Length"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<CustomInput
															type="number"
															name={`goods_details[${index}].goods_length`}
															placeholder="Length in IN"
															isCaps
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_width`}
														label="Width"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<CustomInput
															type="number"
															name={`goods_details[${index}].goods_width`}
															placeholder="Width in IN"
															isCaps
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_height`}
														label="Height"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<CustomInput
															type="number"
															name={`goods_details[${index}].goods_height`}
															placeholder="Height in IN"
															isCaps
														/>
													</FormInput>
												</Col>
												<Col lg={6}>
													<FormInput
														name={`goods_details[${index}].goods_weight`}
														label="Weight"
														labelCol={{ span: 8 }}
														wrapperCol={{ span: 16 }}
													>
														<CustomInput
															type="number"
															name={`goods_details[${index}].goods_weight`}
															placeholder="Weight in KG"
															isCaps
														/>
													</FormInput>
												</Col>
											</Row>

											<Row gutter={24} align="middle">
												<Col
													lg={24}
													style={{
														display: 'flex',
														justifyContent: 'flex-end',
														alignItems: 'center',
													}}
												>
													{index >= 0 &&
														values.goods_details.length > 1 && (
															<button
																className="button"
																type="button"
																style={{
																	border: 'none',
																	backgroundColor: 'transparent',
																	marginTop: '10px',
																	cursor: 'pointer',
																}}
																onClick={() => {
																	remove(index);
																}}
															>
																<MinusCircleOutlined
																	style={{ fontSize: '16px' }}
																/>
															</button>
														)}
												</Col>
											</Row>
											<div
												style={{
													display: 'flex',
													justifyContent: 'center',
												}}
											>
												<button
													type="button"
													style={{
														border: '1px solid #585858',
														background: 'white',
														padding: '2px 8px',
														borderRadius: '2px',
														cursor: 'pointer',
													}}
													onClick={() => {
														console.log(values);
														push({
															goods_description: '',
															goods_quantity: '',
															goods_uom: '',
															goods_length: '',
															goods_width: '',
															goods_height: '',
															goods_weight: '',
														});
														console.log(values);
													}}
												>
													Add Goods
												</button>
											</div>
										</Box>
									</Col>
								</Row>
							))}
						</>
					)}
				/>
				<Row gutter={32} justify="end" style={{ marginTop: 32 }}>
					<Col>
						<Button
							type="default"
							htmlType="button"
							onClick={handlePreviousClick}
							style={{ marginRight: 16 }}
						>
							Previous
						</Button>
					</Col>
					<Col>
						<Button
							type="primary"
							htmlType="submit"
							disabled={isSubmitting || !isValid}
						>
							Submit
						</Button>
					</Col>
				</Row>
			</Box>
		</Form>
	);
}

CreateLRForm.propTypes = {
	values: PropTypes.object.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
	isValid: PropTypes.bool.isRequired,
	setFieldValue: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	handlePincodeChange: PropTypes.func.isRequired,
	validateEwbNumber: PropTypes.func.isRequired,
	handleBackNavigation: PropTypes.func,
	isCreateLRWithoutId: PropTypes.bool,
	isEditMode: PropTypes.bool,
};
